import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { tap, catchError, of } from 'rxjs';
import { ExceptionManager } from 'src/app/components/core/_infrastructure/classes/handlers/exceptionManager';
import { AuthService } from 'src/app/components/core/_infrastructure/classes/services/auth.service';
import { NotificationService } from 'src/app/components/core/_infrastructure/classes/services/notification.service';
import { NavMenuService } from 'src/app/components/core/components/navmenu/navmenu.service';
import { TwCorteCultivoCamViewModel, TwUnidadInspeccionViewModelExtended, CultivosAforosViewModel } from '../../unidad-inspeccion-datosbasicos.types';
import { UnidadInspeccionService } from '../../unidad-inspeccion.service';
import { ComponentBase } from 'src/app/components/core/_infrastructure/classes/componentBase';
import { CultivoItemViewModel } from '../../../cultivos/cultivo.types';
import { EnumCodigosTwTcAcciones } from 'src/app/components/_importacion/solicitudes/types/enum';
import { TwInspeccionCamViewModel } from '../../inspeccion.types';
import { CorteCultivoCamService } from '../../corte-cultivo.service';
import { ConfirmationService } from 'primeng/api';

@Component({
    selector: 'app-inspeccion-ui-aforo-corte-wrapper',
    templateUrl: './inspeccion-ui-aforo-corte-wrapper.component.html',
    styleUrls: ['./inspeccion-ui-aforo-corte-wrapper.component.scss'],
})
export class InspeccionUiAforoCorteWrapperComponent extends ComponentBase implements OnInit {
    cultivos: CultivoItemViewModel[] = [];
    cultivosAforosCortes: CultivosAforosViewModel[];
    formGroup: FormGroup;
    title = { plural: 'Aforos y Cortes/Autorizaciones de Kgs. por Cultivo', singular: 'Aforo y Corte/Autorización Kgs. por Cultivo' };
    unidadInspeccion: TwUnidadInspeccionViewModelExtended;
    inspecciones: TwInspeccionCamViewModel[];
    uiHabilitadaParaCortes = false;

    private listUrl = '/campanias/unidadesinspeccion';
    private aforosCortesUrl = 'campanias/unidadinspeccion/aforosycortes/';
    private deleteSuccessMessage = 'El corte se ha eliminado correctamente';

    private activatedRoute$ = this.activatedRoute.paramMap.subscribe(params => {
        const IdUi = params.get('idUi');
        if (IdUi) {
            this.getUnidadInspeccion(parseInt(IdUi));
        }
    });

    constructor(
        private fb: FormBuilder,
        private activatedRoute: ActivatedRoute,
        private router: Router,
        private unidadInspeccionService: UnidadInspeccionService,
        private corteCultivoCamService: CorteCultivoCamService,
        private navMenuService: NavMenuService,
        private confirmationService: ConfirmationService,
        protected authService: AuthService,
        protected notificationService: NotificationService,
        protected exceptionHandler: ExceptionManager,
    ) {
        super(authService, notificationService, exceptionHandler);
    }

    ngOnInit(): void {
        this.preparePresentation();
    }

    private preparePresentation(): void {
        this.buildForm();
    }

    private buildForm(): void {
        this.formGroup = this.fb.group({
            Campania: [null],
            Periodo: [null],
            DescripcionComunidad: [null],
            EntidadAuditora: [null],
            TipoRecinto: [null], // Tipo de Solicitud
            Operador: [null],
        });
    }

    /**
     * Se cargan los datos de la unidad de inspección y sus recintos "PI-A-Ed" asociados
     * Una vez obtenida, establece el valor para la propiedad readonly a partir de la información del usuario y los permisos de edición.
     * Después invoca al servicio de obtención de inspecciones de la U.I.
     * @param id number
     */
    private getUnidadInspeccion(id: number): void {
        this.unidadInspeccionService
            .getUI(id)
            .pipe(
                tap((udInspeccion: TwUnidadInspeccionViewModelExtended) => {
                    // Una UI existente deberá tener al menos un recinto "PI-A-Ed".
                    if (!udInspeccion?.FirstRecinto) {
                        // No debería pasar por aquí, porque cuando se eliminan todos los recintos "PI-A-Ed" de una UI, se elimina además la UI
                        return;
                    }
                    this.unidadInspeccion = udInspeccion;
                    this.onUnidadInspeccionLoaded();
                }),
                catchError(error => {
                    this.catchServerError(error);
                    return of(null);
                }),
            )
            .subscribe();
    }

    private onUnidadInspeccionLoaded(): void {
        this.navMenuService.updateMenuServiceForUdInspeccion(
            this.unidadInspeccion.IdUi!,
            this.unidadInspeccion.IdEstadoUi!,
            this.unidadInspeccion.FirstRecinto.IdTipoSolicitudCam || 0,
            this.unidadInspeccion.PeriodoCertificaKgs,
        );

        // if (this.hasVerCorteAccess) {
        this.checkUIHabilitadaParaCortes(this.unidadInspeccion.IdUi!);
        // }
    }

    /**
     * Carga la lista de inspecciones de la U.I.
     * @param idUi number
     */
    private checkUIHabilitadaParaCortes(idUi: number): void {
        this.unidadInspeccionService
            .checkHabilitadaParaCortes(idUi)
            .pipe(
                tap((habilitada: boolean) => {
                    this.uiHabilitadaParaCortes = habilitada;
                    if (this.uiHabilitadaParaCortes) {
                        this.getCultivosAforosCortes(idUi);
                    }
                }),
                catchError(error => {
                    this.uiHabilitadaParaCortes = false;
                    this.catchServerError(error);
                    this.formGroup.disable();
                    return of(null);
                }),
            )
            .subscribe();
    }

    private getCultivosAforosCortes(idUi: number): void {
        this.unidadInspeccionService
            .getCultivosAforosCortesByIdUi(idUi)
            .pipe(
                tap((res: CultivosAforosViewModel[]) => {
                    this.cultivosAforosCortes = res;
                    this.calcularProduccionRestanteCultivos();
                }),
                catchError(error => {
                    this.catchServerError(error);
                    this.formGroup.disable();
                    return of(null);
                }),
            )
            .subscribe();
    }

    private calcularProduccionRestanteCultivos(): void {
        this.cultivosAforosCortes.forEach(cultivoAforo => {
            // TODO: scalero1 - tener en cuenta la producción real o la producción estimada ?
            if (cultivoAforo.KgsProduccionReal) {
                cultivoAforo.KgsProduccionRestante = this.calcularProduccionRestante(cultivoAforo.KgsProduccionReal, cultivoAforo.cortesCultivos);
            } else {
                cultivoAforo.KgsProduccionRestante = this.calcularProduccionRestante(cultivoAforo.KgsProduccionEstimada!, cultivoAforo.cortesCultivos);
            }
        });
    }

    private calcularProduccionRestante(produccionAforada: number, cortes: TwCorteCultivoCamViewModel[]): number {
        let produccionRestante = produccionAforada;
        for (let j = 0; j < cortes.length; j++) {
            const corte = cortes[j];
            if (corte.IdUsuarioBorrado === null) {
                produccionRestante = produccionRestante - corte.KgsAutorizados;
            }
        }
        return produccionRestante;
    }

    onBack(): void {
        this.router.navigate([this.listUrl]);
    }

    onDelete(corte: TwCorteCultivoCamViewModel): void {
        this.confirmationService.confirm({
            message: 'Va a eliminar el corte seleccionado, ¿está seguro?',
            accept: () => {
                this.doDeleteCorte(corte);
            },
        });
    }

    private doDeleteCorte(corte: TwCorteCultivoCamViewModel): void {
        this.corteCultivoCamService
            .deleteCorte(corte.Id)
            .pipe(
                tap((res: any) => {
                    this.addSuccessMessage(this.deleteSuccessMessage);
                    this.getUnidadInspeccion(this.unidadInspeccion.IdUi!); // Volvemos a cargar la UI y la lista de cortes de la UI.
                }),
                catchError(error => {
                    this.catchServerError(error);
                    this.formGroup.disable();
                    return of(null);
                }),
            )
            .subscribe();
    }

    onView(corte: TwCorteCultivoCamViewModel): void {
        this.router.navigate([this.aforosCortesUrl, this.unidadInspeccion.IdUi, 'detail', corte.Id]);
    }

    onCrearCorte(): void {
        this.router.navigate([this.aforosCortesUrl, this.unidadInspeccion.IdUi, 'create']);
    }

    get hasDeleteCorteAccess(): boolean {
        if (!this.unidadInspeccion) return false;

        const hasAccess = this.loggedInUser!.getAction(
            EnumCodigosTwTcAcciones.REMOVE_CORTE_UI,
            this.unidadInspeccion.IdEstadoUi,
            this.unidadInspeccion.FirstRecinto.IdTipoSolicitudCam,
            this.unidadInspeccion.PeriodoCertificaKgs ? 1 : 0,
        ).Editable;

        return hasAccess;
    }

    get actionNotAllowed(): boolean {
        return !this.unidadInspeccion.FirstRecinto?.PeriodoEnPlazoVigente || !this.uiHabilitadaParaCortes;
    }

    get hasVerCorteAccess(): boolean {
        if (!this.unidadInspeccion) return false;

        const hasAccess = this.loggedInUser!.getAction(
            EnumCodigosTwTcAcciones.CONS_CORTE_UI,
            this.unidadInspeccion.IdEstadoUi,
            this.unidadInspeccion.FirstRecinto.IdTipoSolicitudCam,
            this.unidadInspeccion.PeriodoCertificaKgs ? 1 : 0,
        ).Visible;

        return hasAccess;
    }

    get hasAccessCreateCorte(): boolean {
        if (!this.unidadInspeccion) return false;

        const hasAccess = this.loggedInUser!.getAction(
            EnumCodigosTwTcAcciones.ADD_CORTE_UI,
            this.unidadInspeccion.IdEstadoUi,
            this.unidadInspeccion.FirstRecinto.IdTipoSolicitudCam,
            this.unidadInspeccion.PeriodoCertificaKgs ? 1 : 0,
        ).Visible;

        return hasAccess;
    }
}
